import React, { memo, useCallback, useEffect, useState } from "react";
import axios from "../../../../axios_config";
import styled from "styled-components";
import { BASE_API_URL } from "../../../../common/endpoints";
import LessonDuration from "./lesson_duration";
import {
  convertHoursAndMinutesToMinutes,
  convertMinutesToHoursAndMinutes,
} from "../../../../common/utils/functions/time_functions";
import ClosedLessonNotification from "./closed_lesson_notification";
import LessonStatus from "./lesson_status";
import LessonOpenDatePicker from "./lesson_open_date_picker";
import LessonTitle from "./lesson_title";
import AdminPageSettingsButtons from "../../admin_page_components/admin_page_settings_buttons";
import { compareObjects } from "../../../../common/utils/functions/compare_objects";
import UnsavedChangesModalWindow from "../../../../common/components/modal_windows/unsaved_changes_modal_window";

const LessonSettingsWrapper = styled.div`
  position: relative;
  width: 437px;
`;

const LessonSettingsContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 26px 24px;
  width: 434px;
  border-left: 1px solid #828282;
  min-height: 220px;
`;

const AdminPageCourseLessonSettings = ({
  lessonSettingsData,
  setLessonSettingsData,
  lessonSettingsMemoData,
  setLessonSettingsMemoData,
  openEditLessonModal,
  setOpenEditLessonModal,
  closeLessonSettings,
  fetchCourse,
}) => {
  const [showHoursesOptions, setShowHoursesOptions] = useState(false);
  const [showMinutesOptions, setShowMinutesOptions] = useState(false);
  const [lessonHourses, setLessonHourses] = useState(0);
  const [lessonMinutes, setLessonMinutes] = useState(0);
  const [showCheckIcon, setShowCheckIcon] = useState(false);

  useEffect(() => {
    const unequal = !compareObjects(lessonSettingsData, lessonSettingsMemoData);

    if (unequal) {
      setShowCheckIcon(false);
    } else {
      setShowCheckIcon(true);
    }
  }, [lessonSettingsData, lessonSettingsMemoData]);

  useEffect(() => {
    if (!lessonSettingsData._id) {
      setShowHoursesOptions(false);
      setShowMinutesOptions(false);
    }
  }, [lessonSettingsData._id]);

  useEffect(() => {
    if (lessonSettingsData._id) {
      const { hours, minutes } = convertMinutesToHoursAndMinutes(
        lessonSettingsData.lesson_duration_min
      );

      setLessonHourses(hours);
      setLessonMinutes(minutes);
    }
  }, [lessonSettingsData]);

  const onClickSaveButton = useCallback(async () => {
    try {
      const { _id } = lessonSettingsData;

      const response = await axios.patch(
        `${BASE_API_URL}/lessons/${_id}`,
        lessonSettingsData
      );

      setLessonSettingsData(response.data);
      setLessonSettingsMemoData(response.data);

      await fetchCourse();
    } catch (error) {
      console.error("Error when saving lesson settings:", error);
    }
  }, [lessonSettingsData, fetchCourse]);

  const handleShowHoursesOptions = useCallback((event) => {
    event.stopPropagation();
    setShowHoursesOptions((prevState) => !prevState);
    setShowMinutesOptions(false);
  }, []);

  const handleShowMinutesOptions = useCallback((event) => {
    event.stopPropagation();
    setShowMinutesOptions((prevState) => !prevState);
    setShowHoursesOptions(false);
  }, []);

  const handleHourseOptions = useCallback(
    (hourses, event) => {
      event.stopPropagation();
      const newDuration = convertHoursAndMinutesToMinutes(
        hourses,
        lessonMinutes
      );

      setLessonHourses(hourses);
      setLessonSettingsData((prevState) => ({
        ...prevState,
        lesson_duration_min: newDuration,
      }));
      setShowHoursesOptions(false);
    },
    [lessonMinutes, setLessonSettingsData]
  );

  const handleMinutesOptions = useCallback(
    (minutes, event) => {
      event.stopPropagation();
      const newDuration = convertHoursAndMinutesToMinutes(
        lessonHourses,
        minutes
      );

      setLessonMinutes(minutes);
      setLessonSettingsData((prevState) => ({
        ...prevState,
        lesson_duration_min: newDuration,
      }));
      setShowMinutesOptions(false);
    },
    [lessonHourses, setLessonSettingsData]
  );

  const closeAllDurationOptions = useCallback((event) => {
    event.stopPropagation();
    setShowHoursesOptions(false);
    setShowMinutesOptions(false);
  }, []);

  const onClickModalSaveButton = async () => {
    await onClickSaveButton();
    closeLessonSettings();
  };

  return (
    <LessonSettingsWrapper>
      {lessonSettingsData._id && (
        <LessonSettingsContainer
          onClick={closeAllDurationOptions}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <LessonTitle
            lessonSettingsData={lessonSettingsData}
            setLessonSettingsData={setLessonSettingsData}
          />

          <LessonDuration
            lessonHourses={lessonHourses}
            lessonMinutes={lessonMinutes}
            showHoursesOptions={showHoursesOptions}
            showMinutesOptions={showMinutesOptions}
            handleShowHoursesOptions={handleShowHoursesOptions}
            handleShowMinutesOptions={handleShowMinutesOptions}
            handleHourseOptions={handleHourseOptions}
            handleMinutesOptions={handleMinutesOptions}
          />

          <LessonStatus
            lessonSettingsData={lessonSettingsData}
            setLessonSettingsData={setLessonSettingsData}
          />
          <ClosedLessonNotification
            lessonSettingsData={lessonSettingsData}
            setLessonSettingsData={setLessonSettingsData}
          />
          <LessonOpenDatePicker />

          <AdminPageSettingsButtons
            onClickSaveButton={onClickSaveButton}
            onClickCancelButton={closeLessonSettings}
            showCheckIcon={showCheckIcon}
          />
        </LessonSettingsContainer>
      )}

      {openEditLessonModal && (
        <UnsavedChangesModalWindow
          setOpen={setOpenEditLessonModal}
          onSaveSettingsButton={onClickModalSaveButton}
          onClickNotSaveButton={closeLessonSettings}
        />
      )}
    </LessonSettingsWrapper>
  );
};

export default memo(AdminPageCourseLessonSettings);
